import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { hardRefresh } from '../lib/app-update';
import { useTracking } from '../services/runtime';
import { appScroller } from '../lib/app-scroller';

const TRIGGER_PX = 70;
const scrolled = () => (appScroller()?.scrollTop ?? window.scrollY) > 0;
const MAX_PX = 110;
/** Screens where a downward drag means something else (the map pans, the run screen must never reload). */
const DISABLED_PATHS = ['/map', '/run'];
/** Drags starting inside these never refresh: maps, bottom sheets, anything opting out. */
const EXCLUDE = '[data-testid="game-map"], [role="dialog"], [data-no-ptr], input, textarea, select';

/** Pull down from the top of a screen to reload the app (and pick up a new deploy). */
export function PullToRefresh() {
  const { pathname } = useLocation();
  const runState = useTracking((s) => s.state);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<{ y: number; x: number } | null>(null);
  const pullRef = useRef(0);

  const disabled = DISABLED_PATHS.includes(pathname) || runState === 'RUNNING' || runState === 'PAUSED' || runState === 'RESUMING';

  useEffect(() => {
    if (disabled) return;
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1 || scrolled() || refreshing) return;
      if ((e.target as Element | null)?.closest?.(EXCLUDE)) return;
      start.current = { y: t.clientY, x: t.clientX };
    };
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!start.current || !t) return;
      const dy = t.clientY - start.current.y;
      const dx = Math.abs(t.clientX - start.current.x);
      // Finger jitter before the pull really starts.
      if (dy < 10 && dx < 10 && dy > -10) return;
      // Scrolling up, sideways swipes or the page scrolled meanwhile: not a pull.
      if (dy <= 0 || dx > dy || scrolled()) {
        start.current = null;
        pullRef.current = 0;
        setPull(0);
        return;
      }
      pullRef.current = Math.min(MAX_PX, dy * 0.5);
      setPull(pullRef.current);
    };
    const onEnd = () => {
      if (!start.current) return;
      start.current = null;
      if (pullRef.current >= TRIGGER_PX) {
        setRefreshing(true);
        setPull(TRIGGER_PX);
        void hardRefresh();
      } else {
        setPull(0);
      }
      pullRef.current = 0;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [disabled, refreshing]);

  if (pull <= 0 && !refreshing) return null;
  const ready = pull >= TRIGGER_PX;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[70] flex justify-center"
      style={{ top: `calc(var(--safe-top) + ${Math.max(0, pull - 40)}px)` }}
      data-testid="pull-to-refresh"
    >
      <div className="flex size-10 items-center justify-center rounded-full border border-ink-700 bg-ink-850 shadow-lg">
        <svg
          viewBox="0 0 24 24"
          className={`size-5 ${ready ? 'text-volt' : 'text-ink-300'} ${refreshing ? 'animate-spin' : ''}`}
          style={refreshing ? undefined : { transform: `rotate(${(pull / TRIGGER_PX) * 270}deg)` }}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M20 12a8 8 0 1 1-2.34-5.66" />
          <path d="M20 4v4h-4" />
        </svg>
      </div>
    </div>
  );
}
